import React, { useState, useCallback } from 'react';
import {
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Box,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  IconButton,
  Stack,
  Fade,
  Skeleton
} from '@mui/material';
import {
  Image as ImageIcon,
  PhotoLibrary,
  Delete,
  CheckBox,
  Refresh,
  ZoomIn
} from '@mui/icons-material';
import { useBook } from '../../Context/BookContext';
import LoadingState from '../LoadingState';
import { SnackbarState } from '../../types';
import { authenticatedGet, authenticatedPost } from '../../utils/api';
import { useAuth } from '../../Context/AuthContext';

const getUserImagesEndpoint = process.env.REACT_APP_LAMBDA_GET_USER_IMAGES;
const removeImagesEndpoint = process.env.REACT_APP_LAMBDA_REMOVE_IMAGE;

interface UserImage {
  compositeKey: string;
  imageUrl: string;
  subject: string;
  prompt: string;
  timestamp: number;
}

interface UserImagesResponse {
  images: UserImage[];
}

interface DeleteResult {
  success: boolean;
  compositeKey: string;
  error?: any;
}

const ImageGeneratorPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { selectedBook } = useBook();
  const [images, setImages] = useState<UserImage[]>([]);
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [isDeletingImages, setIsDeletingImages] = useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<UserImage | null>(null);



  const fetchUserImages = useCallback(async (): Promise<void> => {
    if (!selectedBook || !getUserImagesEndpoint || !isAuthenticated || !user?.userId) return;

    setLoadingImages(true);

    try {
      const response = await authenticatedGet<UserImagesResponse>(getUserImagesEndpoint, {
        params: {
          FileId: selectedBook.FileId
        }
      });

      console.log('User images response:', response.data);
      setImages(response.data.images || []);
    } catch (error) {
      console.error('Error fetching user images:', error);
    } finally {
      setLoadingImages(false);
    }
  }, [selectedBook, isAuthenticated, user?.userId]);

  React.useEffect(() => {
    if (selectedBook) {
      fetchUserImages();
    }
  }, [selectedBook, fetchUserImages]);

  const handleImageSelection = (compositeKey: string): void => {
    setSelectedImages(prev => {
      if (prev.includes(compositeKey)) {
        return prev.filter(key => key !== compositeKey);
      } else {
        return [...prev, compositeKey];
      }
    });
  };



  const handleDeleteSelectedImages = async (): Promise<void> => {
    if (selectedImages.length === 0) return;
    setConfirmDelete(true);
  };

  const confirmDeleteImages = async (): Promise<void> => {
    if (!removeImagesEndpoint || !isAuthenticated || !user?.userId) return;

    setConfirmDelete(false);
    setIsDeletingImages(true);

    try {
      const results: DeleteResult[] = await Promise.all(
        selectedImages.map(async (compositeKey: string): Promise<DeleteResult> => {
          try {
            await authenticatedPost(removeImagesEndpoint!, {
              CompositeKey: compositeKey
            });
            return { success: true, compositeKey };
          } catch (error) {
            return { success: false, compositeKey, error };
          }
        })
      );

      const successCount = results.filter(r => r.success).length;
      await fetchUserImages();
      setSelectedImages([]);

      setSnackbar({
        open: true,
        message: `Successfully deleted ${successCount} of ${selectedImages.length} images`,
        severity: successCount === selectedImages.length ? 'success' : 'warning'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error deleting images',
        severity: 'error'
      });
    } finally {
      setIsDeletingImages(false);
    }
  };

  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const handleCloseSnackbar = (): void => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (!selectedBook) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, maxWidth: 600, mx: 'auto' }}>
        <PhotoLibrary sx={{ fontSize: 80, color: 'text.disabled', mb: 3 }} />
        <Typography variant="h4" sx={{ color: 'text.primary', mb: 2, fontWeight: 600 }}>
          No Book Selected
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
          Please select a book from your Library to view its images
        </Typography>
        <Button
          variant="contained"
          sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
          href="/"
        >
          Go to Library
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'text.primary', mb: 1, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Image Gallery
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1, fontSize: { xs: '0.9rem', md: '1rem' } }}>
          View and manage AI images for "{selectedBook.Title || selectedBook.FileName}"
        </Typography>
        <Typography variant="body2" sx={{ color: 'success.main', fontStyle: 'italic', fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
          Images are automatically added to your book when generated through the image generator
        </Typography>
      </Box>

      {/* Action Bar */}
      <Card sx={{ backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider', mb: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={fetchUserImages}
                  disabled={loadingImages}
                  sx={{
                    color: 'text.secondary',
                    borderColor: 'action.disabled',
                    textTransform: 'none'
                  }}
                >
                  Refresh
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
                <Chip
                  icon={<ImageIcon />}
                  label={`${images.length} images`}
                  sx={{
                    backgroundColor: 'action.hover',
                    color: 'text.primary',
                    fontWeight: 500
                  }}
                />
                {selectedImages.length > 0 && (
                  <Chip
                    icon={<CheckBox />}
                    label={`${selectedImages.length} selected`}
                    sx={{
                      backgroundColor: 'primary.main',
                      color: '#fff',
                      fontWeight: 500
                    }}
                  />
                )}
                {selectedImages.length > 0 && (
                  <Button
                    variant="outlined"
                    startIcon={<Delete />}
                    onClick={handleDeleteSelectedImages}
                    disabled={isDeletingImages}
                    sx={{
                      color: 'error.main',
                      borderColor: 'error.dark',
                      '&:hover': {
                        borderColor: 'error.main',
                        backgroundColor: 'error.light',
                      },
                      textTransform: 'none'
                    }}
                  >
                    Delete Selected
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>


        </CardContent>
      </Card>

      {/* Image Gallery */}
      {loadingImages ? (
        <Grid container spacing={3}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card sx={{ backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                <Skeleton variant="rectangular" height={200} />
                <CardContent>
                  <Skeleton variant="text" />
                  <Skeleton variant="text" width="60%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : images.length > 0 ? (
        <Grid container spacing={3}>
          {images.map((image) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={image.compositeKey}>
              <Fade in timeout={300}>
                <Card sx={{
                  backgroundColor: 'background.paper',
                  border: selectedImages.includes(image.compositeKey)
                    ? '2px solid'
                    : '1px solid',
                  borderColor: selectedImages.includes(image.compositeKey) 
                    ? 'primary.main' 
                    : 'divider',
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    borderColor: 'primary.main'
                  }
                }}>
                  <Box
                    sx={{ position: 'relative' }}
                    onClick={() => handleImageSelection(image.compositeKey)}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={image.imageUrl}
                      alt={`Generated image of ${image.subject}`}
                      sx={{
                        objectFit: 'cover',
                        backgroundColor: 'action.hover'
                      }}
                    />

                    {/* Selection Indicator */}
                    <Box sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: selectedImages.includes(image.compositeKey)
                        ? 'primary.main'
                        : 'rgba(0,0,0,0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff'
                    }}>
                      {selectedImages.includes(image.compositeKey) ? '✓' : ''}
                    </Box>

                    {/* Preview Button */}
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: '#fff',
                        '&:hover': {
                          backgroundColor: 'rgba(0,0,0,0.7)'
                        }
                      }}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewImage(image);
                      }}
                    >
                      <ZoomIn />
                    </IconButton>
                  </Box>

                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: '1rem',
                      mb: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {image.subject}
                    </Typography>

                    <Typography variant="caption" sx={{
                      color: 'text.secondary',
                      display: 'block',
                      mb: 1
                    }}>
                      {formatTimestamp(image.timestamp)}
                    </Typography>

                    <Typography variant="body2" sx={{
                      color: 'text.secondary',
                      fontSize: '0.8rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {image.prompt}
                    </Typography>
                  </CardContent>
                </Card>
              </Fade>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card sx={{ backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <ImageIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 3 }} />
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
              No Images Available
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Generate images from your Library page to see them here. Once created, you can view, manage and add them to your book.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Image Preview Dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }
        }}
      >
        {previewImage && (
          <>
            <DialogTitle sx={{ color: 'text.primary' }}>
              {previewImage.subject}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <img
                  src={previewImage.imageUrl}
                  alt={previewImage.subject}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                <strong>Prompt:</strong> {previewImage.prompt}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Created: {formatTimestamp(previewImage.timestamp)}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewImage(null)} sx={{ color: 'text.secondary' }}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }
        }}
      >
        <DialogTitle sx={{ color: '#f8fafc' }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete {selectedImages.length} selected {selectedImages.length === 1 ? 'image' : 'images'}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button onClick={confirmDeleteImages} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading States */}
      {isDeletingImages && <LoadingState message="Deleting selected images..." />}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ImageGeneratorPage; 